/**
 * The author's words about a character.
 *
 * His text, not theirs: the character page shows this to readers, and the person
 * holding the character writes nothing there. The note is read from the server when
 * the dialog opens rather than carried in the row — the People table is built from
 * tags, invitations and accounts, and does not otherwise know about wallets or notes.
 */

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { apiService } from '../../services/api'
import { logger } from '../../utils/logger'

interface CharacterNoteDialogProps {
  open: boolean
  bookId: string
  tagId: string
  name: string
  onClose: () => void
}

export const CharacterNoteDialog: React.FC<CharacterNoteDialogProps> = ({ open, bookId, tagId, name, onClose }) => {
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    setLoading(true)
    setNote('')

    let cancelled = false
    apiService
      .getCharacter(bookId, tagId)
      .then((c) => {
        if (!cancelled) {
          setNote(c.authorNote ?? '')
        }
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error('Failed to load the character note', err)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [open, bookId, tagId])

  const handleSave = async () => {
    setSaving(true)
    try {
      await apiService.saveCharacterNote(bookId, tagId, note)
      toast.success('נשמר')
      onClose()
    } catch (err: any) {
      toast.error(err?.message || 'לא הצלחנו לשמור')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth dir="rtl">
      <DialogTitle sx={{ textAlign: 'right' }}>על {name}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 600 }}
            helperText="מוצג לקוראים בעמוד של הדמות. השאר ריק כדי להסיר."
            sx={{ mt: 0.5 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'row-reverse', justifyContent: 'flex-start', gap: 1, px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          ביטול
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          שמירה
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CharacterNoteDialog
